import { Box } from '@mui/material'
import { DailyOrderType } from 'constant'
import { BoxOrderContext } from 'contexts/BoxOrderContextProvider'
import { OrderContext } from 'contexts/OrderContextProvider'
import useDailyOrder from 'hooks/useDailyOrder'
import { useContext, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import PickClass from './PickClass'
import PickRack from './PickRack'
import ScanBox from './ScanBox'
import ScanItem from './ScanItem'
import PickLocation from './PickLocation'
import { LoadingContext } from 'contexts/LoadingContextProvider'

const PickItemToBox = () => {
  const { company } = useParams()
  const navigate = useNavigate()
  const { fetchDailyOrders, groupItemsByClassAndRack } = useDailyOrder()
  const {
    selectedLocationId,
    filteredDailyOrdersByClass,
    selectedDailyOrderClass,
    selectedDailyOrderBatchIndex
  } = useContext(OrderContext)
  const {
    selectedClass,
    selectedRack,
    boxCode,
    setBoxItems,
    setSelectedClass,
    setSelectedRack,
    setBoxCode,
    setRacks
  } = useContext(BoxOrderContext)
  const { setLoading } = useContext(LoadingContext)

  const hasOrderBatchContext =
    Boolean(selectedDailyOrderClass) &&
    selectedDailyOrderBatchIndex !== undefined

  useEffect(() => {
    if (!company || hasOrderBatchContext) return
    setSelectedClass(null)
    setSelectedRack(null)
    setBoxCode('')
    setRacks([])
    setBoxItems([])
    navigate(`/${company}/daily-order/box-code-order`, { replace: true })
  }, [
    company,
    hasOrderBatchContext,
    navigate,
    setBoxCode,
    setBoxItems,
    setRacks,
    setSelectedClass,
    setSelectedRack
  ])

  const setBoxItemsRef = useRef(setBoxItems)
  setBoxItemsRef.current = setBoxItems
  const setLoadingRef = useRef(setLoading)
  setLoadingRef.current = setLoading
  /** Ignore stale responses when multiple getOrders overlap (network reorder). */
  const fetchSeqRef = useRef(0)

  /** Stable key so effect deps are not invalidated by new array refs every render. */
  const batchContextKey = useMemo(() => {
    const orderKey = filteredDailyOrdersByClass
      .map((o: any) => `${o.orderNumber}:${o.batchLabel ?? ''}`)
      .sort()
      .join('|')
    return `${selectedDailyOrderBatchIndex ?? ''}|${orderKey}`
  }, [filteredDailyOrdersByClass, selectedDailyOrderBatchIndex])

  const fetchDailyOrdersRef = useRef(fetchDailyOrders)
  fetchDailyOrdersRef.current = fetchDailyOrders
  const groupItemsByClassAndRackRef = useRef(groupItemsByClassAndRack)
  groupItemsByClassAndRackRef.current = groupItemsByClassAndRack
  const filteredOrdersRef = useRef(filteredDailyOrdersByClass)
  filteredOrdersRef.current = filteredDailyOrdersByClass
  const batchIndexRef = useRef(selectedDailyOrderBatchIndex)
  batchIndexRef.current = selectedDailyOrderBatchIndex

  const getBatchIndexByLabel = (label?: string | null): number => {
    const normalized = `${label || ''}`.trim().toUpperCase()
    if (!/^[A-Z]+$/.test(normalized)) return Number.MAX_SAFE_INTEGER
    let result = 0
    for (let i = 0; i < normalized.length; i++) {
      result = result * 26 + (normalized.charCodeAt(i) - 64)
    }
    return result - 1
  }
  const getBatchLabels = (orders: any[]) => {
    return Array.from(
      new Set(
        orders
          .map(order => `${order?.batchLabel || ''}`.trim().toUpperCase())
          .filter((label: string) => label.length > 0)
      )
    ).sort((a, b) => getBatchIndexByLabel(a) - getBatchIndexByLabel(b))
  }
  useEffect(() => {
    if (!hasOrderBatchContext) return

    const seq = ++fetchSeqRef.current
    setLoadingRef.current(true)
    ;(async () => {
      try {
        const orders = await fetchDailyOrdersRef.current(
          `${company}`,
          DailyOrderType.BoxCodeSalesOrder,
          undefined,
          true
        )
        if (seq !== fetchSeqRef.current) return

        const filtered = filteredOrdersRef.current
        const batchIdx = batchIndexRef.current
        let targetOrders = orders
        if (filtered.length > 0 && batchIdx !== undefined) {
          const labels = getBatchLabels(filtered)
          const selectedBatchLabel = labels[batchIdx]
          const selectedBatchOrders = filtered.filter(
            (order: any) =>
              `${order?.batchLabel || ''}`.trim().toUpperCase() ===
              selectedBatchLabel
          )
          const selectedOrderNumbers = new Set(
            selectedBatchOrders.map((order: any) => order.orderNumber)
          )
          targetOrders = orders.filter((order: any) =>
            selectedOrderNumbers.has(order.orderNumber)
          )
        }
        if (seq !== fetchSeqRef.current) return

        const items = groupItemsByClassAndRackRef.current(
          targetOrders.flatMap(order => order.orderItems)
        )
        setBoxItemsRef.current(items)
      } finally {
        if (seq === fetchSeqRef.current) {
          setLoadingRef.current(false)
        }
      }
    })()
  }, [company, batchContextKey, hasOrderBatchContext])

  if (!company || !hasOrderBatchContext) {
    return null
  }

  return (
    <Box>
      {!selectedLocationId ? (
        <PickLocation />
      ) : !selectedClass ? (
        <PickClass />
      ) : !selectedRack ? (
        <PickRack />
      ) : !boxCode ? (
        <ScanBox />
      ) : (
        <ScanItem />
      )}
    </Box>
  )
}

export default PickItemToBox
